
import { defineComponent, computed, onMounted, ref, onUnmounted, ComponentPublicInstance } from "vue";
import {
  FridgeDetailsMylist,
  FridgeDetailsSettingsList,
  FridgeDetailsMylistItem,
  FRIDGE_DETAILS_COOLING_MODE,
  FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING,
  FridgeCoolingAssistSettingItem,
  FridgeDetailsCoolingAssistSettingList
} from "@/types/fridgeDetails";
import NativeClient, { TitleInfo } from "@/client/native-client";
import store from "@/store";
import router from "@/router";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const"; 
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil, { translateYYYYMMDDToJapan } from "@/utils/common-utils";
import { translateFridgeDetailsSettingsToBuffer } from "@/utils/fridgeDetails-utils";
import FridgeDetailsTab from "@/components/fridgeDetails/FridgeDetailsTab.vue";
import FridgeDetailsMyAssistList from "@/components/fridgeDetails/FridgeDetailsMyAssistList.vue";
import FridgeDetailsConfirmCoolingAssistSettingDialog from "@/components/fridgeDetails/FridgeDetailsConfirmCoolingAssistSettingDialog.vue";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";

export default defineComponent({
  name: "FridgeCoolingAssistPage",
  components: { FridgeDetailsTab, FridgeDetailsMyAssistList, CommonDesignDialog, FridgeDetailsConfirmCoolingAssistSettingDialog },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "クーリングアシスト"));
    CommonUtil.scroll({ top: 0, left: 0 });

    // 選択中のタブのIndex
    const selectTabIndex = ref(0);

    // ダイアログ情報 API実行エラーのダイアログは、割り込まれるので、API実行前に、以下の情報で判別する
    class FridgeCoolingAssistDialogInfo {
      type: number;         // 0:なし、1:運転情報取得API、マイアシスト取得API実行、2:マイアシスト項目送信確認、3:マイアシスト項目送信API実行、4:マイアシスト送信完了
      negativeCallback: (() => void) | null;
      positiveCallback: (() => void) | null;
      constructor(type: number, negativeCallback: (() => void) | null, positiveCallback: (() => void) | null) {
        this.type = type;
        this.negativeCallback = negativeCallback;
        this.positiveCallback = positiveCallback;
      }
    } 
    const refDialogInfo = ref(new FridgeCoolingAssistDialogInfo(0, null, null));

    // ダイアログ
    const isShowDialog = ref(false);
    const dialogType = ref(0);
    const dialogTitle = ref("");
    const dialogMessage = ref("");
    const dialogPositiveButtonLabel = ref("");
    const dialogNegativeButtonLabel = ref("");
    const dialogPositiveHandler = () => {
      isShowDialog.value = false;
      if (refDialogInfo.value.positiveCallback != null) {
        refDialogInfo.value.positiveCallback();
      }
    };
    const dialogNegativeHandler = () => {
      isShowDialog.value = false;
      if (refDialogInfo.value.negativeCallback != null) {
        refDialogInfo.value.negativeCallback();
      }
    };
    const dialogCloseHandler = () => {
      isShowDialog.value = false;
    };

    // 送信確認ダイアログ
    const refConfirmDialogInfo = ref(new FridgeCoolingAssistDialogInfo(0, null, null));
    const isShowConfirmDialog = ref(false);
    const settingsListName = ref("");
    const settingModeName = ref("");
    const settingMinutes = ref(0);
    const settingSeconds = ref(0);
    const isValidSeconds = ref(false);
    const confirmCancel = () => {
      isShowConfirmDialog.value = false;
      if (refConfirmDialogInfo.value.negativeCallback != null) {
        refConfirmDialogInfo.value.negativeCallback();
      }
    };
    const confirmSend = () => {
      isShowConfirmDialog.value = false;
      if (refConfirmDialogInfo.value.positiveCallback != null) {
        refConfirmDialogInfo.value.positiveCallback();
      }
    };

    // 編集モード
    const isEditMode = false;
    const styleMyAssistContents = ref({ height: `calc(100% - 6rem)`});
    const isEditAvailable = computed(() => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage isEditAvailable()");
      const data: FridgeDetailsMylist | null = store.getters["fridgeDetails/getMyAssist"];
      let retValue = false;
      if (data == null || data.mylist_list.length == 0) {
        styleMyAssistContents.value = { height: `calc(100% - 6rem)`};
        retValue = false;
      } else {
        styleMyAssistContents.value = { height: `calc(100% - 12rem)`};
        retValue = true;
      }
      return retValue;
    });
    const myAssistEditButtonAction = () => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage myAssistEditButtonAction()");
      router.push("/fridgeMyAssistListEdit");
    };

    // 定数
    const tabTitleList = ["通常設定", "マイリスト"];
    const coolingAssisTitleText = "クーリングアシスト（はやうま）";
    const settingDescriptionText = "「冷ます、急冷、急凍」の３つのコースをご用意。すばやく冷やすことで料理を手際良く仕上げることができ、幅広い料理に役立ちます。※いつも使う設定を使用したい場合は上部の「マイリスト」から設定してください。\n\n＜注意＞\nビン類や缶ビール、炭酸飲料などは凍ると割れや破裂の恐れがあります。";
    const settingButtonTitleText = "食材を入れて次へ";

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // マイリスト
    const myAssistRegistStateText = computed(() => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage myAssistRegistStateText()");
      const data: FridgeDetailsMylist | null = store.getters["fridgeDetails/getMyAssist"];
      if (data == null) {
        return "";
      }
      if (data.mylist_list)
      return `登録件数：${data.mylist_list.length}/${data.mylist_max_num}件`;
    });
    const myAssistList = computed(() => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage myAssistList()");
      const data: FridgeDetailsMylist | null = store.getters["fridgeDetails/getMyAssist"];
      if (data == null) {
        return null;
      }
      NativeClient.sendDebugLog("FridgeCoolingAssistPage myAssistList() list = " + JSON.stringify(data.mylist_list));
      return data.mylist_list;
    });

    //
    // API実行
    //

    // ダイアログ表示の制御
    class FridgeCoolingAssistPageDialogType {
      type: number; // 0:ダイアログ表示なし、1:データ所得、2:マイリスト削除
      deleteId: number; // 削除するマイリストID
      constructor(type: number, deleteId: number) {
        this.type = type;
        this.deleteId = deleteId;
      }
    }
    const getData = async () => {
      NativeClient.sendDebugLog("FridgeCoolingAssist getData()");
      refDialogInfo.value = new FridgeCoolingAssistDialogInfo(1, null, getData);
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await Promise.all([
        store.dispatch("fridgeDetails/getCoolingAssistSettings"),
        // store.dispatch("fridgeDetails/getSettings"),
        // store.dispatch("fridgeDetails/getFunctionList"),
        store.dispatch("fridgeDetails/getMyAssist")
      ]).then(result => {
        store.dispatch("common/stopLoading");
        updateDisplay();
      });
    };

    // スクロール
    const scroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const setScrollTo = (position: number) => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage setScrollTo scrollWidth = " + scroll.value?.scrollWidth);
      if (scroll.value != undefined) {
        NativeClient.sendDebugLog("FridgeCoolingAssistPage setScrollTo scroll to left = " + scroll.value!.scrollWidth * position );
        //scroll.value!.scrollTo({ top: 0, left: scroll.value!.scrollWidth / tabTitleList.length * position });
        scroll.value!.scrollTo({ top: 0, left: scroll.value!.scrollWidth / 3 * position });
      }
    };
    const changeTab = (index: number) => {
      selectTabIndex.value = index;
      setScrollTo(index);
      store.dispatch("fridgeDetails/updateCoolingAssistTopTab", index);
    };

    // タブの初期値
    const initialTabIndex = computed(() => {
      let retIndex = 0;
      const index: number | null = store.getters["fridgeDetails/getCoolingAssistTopTab"];
      if (index != null) {
        selectTabIndex.value = index;
        retIndex = index;
        NativeClient.sendDebugLog("FridgeCoolingAssistPage initialTabIndex index != null")
        setScrollTo(index);
      } else {
        if (router.currentRoute.value.query.tab == "myAssist") {
          // マイリストを指定
          retIndex = 1;
          NativeClient.sendDebugLog("FridgeCoolingAssistPage initialTabIndex myAssist");
        }
      }
      selectTabIndex.value = retIndex;
      setScrollTo(retIndex);
      return retIndex;
    });

    // 表示の制御
    onMounted(() => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage onMounted()");

      // ページIDは未決定
      // const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, null, []);
      // NativeClient.sendViewedLog(logInfo);
      getData();
    });

    //
    // ボタン類
    //
    // クーリングアシスト・ボタン
    const styleSettingButton = computed(() => {
      const data: FridgeDetailsSettingsList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      if (data == null) {
        // 取得できていない
        return { backgroundColor: `rgba(51,51,51,0.4)`};
      }
      return { backgroundColor: `#333333`};
    });
    const settingButtonAction = () => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage settingButtonAction()");
      const data: FridgeDetailsSettingsList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      if (data == null) {
        return;
      }
      router.push("/fridgeCoolingAssistSettings");
    };
    // マイリスト追加
    const isDisplayAppendButton = computed(() => {
      let retValue = false;
      const data: FridgeDetailsMylist | null = store.getters["fridgeDetails/getMyAssist"];
      if (data != null && data.mylist_list.length < data.mylist_max_num) {
        retValue = true;
      } else {
        retValue = false;
      }
      return retValue;
    });
    const myAssistAppendButtonAction = () => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage myAssistAppendButtonAction()");
      router.push("/fridgeMyAssistEditing");
    };
    // マイリスト送信
    let sendingItem: FridgeDetailsMylistItem | null = null;
    const completeAction = () => {
      // 家電TOPに更新要求
      NativeClient.updateApplianceTop();

      // 本画面を閉じる
      NativeClient.pageClose();
    };
    const sendCoolingAssist = async () => {
      const settings: FridgeDetailsCoolingAssistSettingList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      if (sendingItem != null && settings != null) {
        const data = new FridgeCoolingAssistSettingItem();
        data.cooloven_mode = sendingItem.cooloven_mode;
        data.cooloven_time = sendingItem.cooloven_time;
        if (sendingItem.cooloven_mode == FRIDGE_DETAILS_COOLING_MODE.QUENCH) {
          data.cooloven_second = sendingItem.cooloven_second;
        } else {
          data.cooloven_second = undefined;
        }
        refDialogInfo.value = new FridgeCoolingAssistDialogInfo(3, null, sendCoolingAssist)
        store.dispatch("common/startFullscreenLoading");
        await NativeClient.getApiParams();
        await store.dispatch("fridgeDetails/setCoolingAssist", data);
        store.dispatch("common/stopFullscreenLoading");
      }
      // 完了ダイアログ
      dialogType.value = 12;
      dialogTitle.value = "設定完了しました。";
      dialogMessage.value = "";
      dialogPositiveButtonLabel.value = "";
      dialogNegativeButtonLabel.value = "閉じる"; 
      refDialogInfo.value = new FridgeCoolingAssistDialogInfo(4, completeAction, null)
      isShowDialog.value = true;
    };
    const clickMyAssistItem = (id: number) => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage clickMyAssistItem() id = " + id);
      const data: FridgeDetailsMylist | null = store.getters["fridgeDetails/getMyAssist"];
      const settings: FridgeDetailsCoolingAssistSettingList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      if (data != null && settings != null) {
        const item = data.mylist_list.find(element => element.mylist_id == id);
        if (item != undefined && item != null) {
          NativeClient.sendDebugLog("FridgeCoolingAssistPage clickMyAssistItem() item = " + JSON.stringify(item));
          sendingItem = item;
          settingsListName.value = item.nickname;
          refConfirmDialogInfo.value = new FridgeCoolingAssistDialogInfo(2, null, sendCoolingAssist);
          switch (item.cooloven_mode) {
            case FRIDGE_DETAILS_COOLING_MODE.QUENCH:
              if (settings.cooloven_in_seconds) {
                isValidSeconds.value = true;
                settingSeconds.value = item.cooloven_second;
              } else {
                isValidSeconds.value = false;
              }
              settingModeName.value = FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.QUENCH;
              settingMinutes.value = item.cooloven_time;
              break;
            case FRIDGE_DETAILS_COOLING_MODE.COLD:
              isValidSeconds.value = false;
              settingModeName.value = FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.COLD;
              settingMinutes.value = item.cooloven_time;
              break;
            default:
              isValidSeconds.value = false;
              settingModeName.value = FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.FROZEN;
              settingMinutes.value = item.cooloven_time;
              break;
          }
          settingsListName.value = item.nickname;
          isShowConfirmDialog.value = true;
        }
      }
    };
    // マイリスト削除（インターフェースのみ）
    const clickDeleteMyAssistItem = (id: number, name: string) => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage clickDeleteMyAssistItem(id:" + id + ", name: " + name + ")");
    };

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage retryApiErrorHandler");
      closeDialog();
      // if (refDialogInfo.value.positiveCallback != null) {
      //   NativeClient.sendDebugLog("FridgeCoolingAssistPage retryApiErrorHandler positiveCallback exist");
      //   refDialogInfo.value.positiveCallback();
      // }
    };
    const okApiErrorHandler = () => {
      NativeClient.sendDebugLog("FridgeCoolingAssistPage okApiErrorHandler");
      closeDialog();
    };

    return {
      scroll,
      changeTab,
      tabTitleList,
      initialTabIndex,
      coolingAssisTitleText,
      settingDescriptionText,
      settingButtonTitleText,
      settingButtonAction,
      styleSettingButton,
      updateDisplayKey,
      myAssistList,
      myAssistRegistStateText,
      isDisplayAppendButton,
      myAssistEditButtonAction,
      myAssistAppendButtonAction,
      isEditAvailable,
      isEditMode,
      clickMyAssistItem,
      clickDeleteMyAssistItem,
      styleMyAssistContents,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler,
      isShowDialog,
      dialogType,
      dialogTitle,
      dialogMessage,
      dialogPositiveButtonLabel,
      dialogNegativeButtonLabel,
      dialogPositiveHandler,
      dialogNegativeHandler,
      dialogCloseHandler,
      isShowConfirmDialog,
      settingsListName,
      settingModeName,
      settingMinutes,
      settingSeconds,
      isValidSeconds,
      confirmCancel,
      confirmSend
    };
  }
});
